import { marked } from 'marked';
import katex from 'katex';
import DOMPurify from 'isomorphic-dompurify';
import type { QsQuestion, QsExportedData } from './types.ts';
import { PURIFY_CONFIG } from './purify.ts';

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Map difficulty level to database level (1: easy, 2: medium, 3: hard)
 */
export const LEVEL_MAPPER: Record<string | number, 1 | 2 | 3> = {
  easy: 1,
  medium: 2,
  hard: 3,
  EASY: 1,
  MEDIUM: 2,
  HARD: 3,
  1: 1,
  2: 2,
  3: 3,
};

/**
 * Map Quiz Supporter format to database question type
 */
export const QUESTION_TYPE_MAPPER: Record<string, string> = {
  'multiple-choice': 'single-choice',
  'true-false': 'boolean',
  'fill-blank': 'text',
  MCQ_4: 'single-choice',
  TRUE_FALSE: 'boolean',
  MULTI: 'multiple-choice',
};


/**
 * Convert LaTeX expressions to HTML using KaTeX
 * Uses stricter regex to avoid false-positives (e.g. "$5 cost")
 */
function convertLatex(content: string): string {
  // Replace block math $$...$$
  let result = content.replace(/\$\$([\s\S]+?)\$\$/g, (_, math) => {
    try {
      return katex.renderToString(math.trim(), {
        throwOnError: false,
        displayMode: true,
      });
    } catch {
      return `$$${math}$$`;
    }
  });

  // Replace inline math $...$
  // Stricter regex: ensures $ is not followed by digit (common in prices)
  // and content contains math-like characters (letters, numbers, operators, braces)
  result = result.replace(/(^|[^\\$])\$([^\$\n]+?)\$(?!\d)/g, (match, prefix, math) => {
    // Only treat as math if content contains math-like characters
    if (!/[a-zA-Z0-9\\^_{}()[\]+\-=/*<>,.]/.test(math.trim())) {
      return match;
    }
    try {
      return prefix + katex.renderToString(math.trim(), { throwOnError: false });
    } catch {
      return match;
    }
  });

  return result;
}

/**
 * Convert Markdown/LaTeX to sanitized HTML
 *
 * SECURITY: Output is sanitized via DOMPurify to prevent XSS from
 * malicious content in iframe messages.
 *
 * @param content - Markdown/LaTeX content string
 * @returns Sanitized HTML string safe for rendering
 */
export function convertToHtml(content: string): string {
  if (!content) return '';
  const withLatex = convertLatex(content);
  const html = marked.parse(withLatex, { async: false }) as string;
  return DOMPurify.sanitize(html, PURIFY_CONFIG) as string;
}

/**
 * Map Quiz Supporter question to database question format
 */
export function mapQuestionToDb(q: QsQuestion) {
  const content = q.content ?? q.stem ?? '';
  const format = q.format ?? '';
  const level = q.level !== undefined ? (LEVEL_MAPPER[q.level] || 1) : 1;

  return {
    content: convertToHtml(content),
    type: QUESTION_TYPE_MAPPER[format] || 'single-choice',
    level,
    options: (q.options || []).map((opt) => ({
      content: convertToHtml(opt.content ?? opt.text ?? ''),
      isCorrect: Boolean(opt.isCorrect),
    })),
    explanation: q.explanation
      ? convertToHtml(q.explanation)
      : undefined,
  };
}

/**
 * Convert full exported data to DB format questions
 */
export function mapExportedToDbQuestions(
  data: QsExportedData
): ReturnType<typeof mapQuestionToDb>[] {
  if (!data || !Array.isArray(data.questions)) {
    return [];
  }
  return data.questions.map(mapQuestionToDb);
}

/**
 * The `quiz-supporter:exported` wire payload.
 *
 * This is the shape the embed actually posts when the teacher taps "Sử dụng kết quả": the
 * question text is `stem`, the answers are `text`, and difficulty/format describe the whole set
 * rather than each question. It differs from the older `exported` payload, which nests questions
 * under `payload` and carries per-question level/format.
 *
 * Mirrors the mapping the admin app uses (PopupGenerateQuestionFromQS/index.vue) so both hosts
 * read the same fields.
 */
export interface QsLegacyExportedQuestion {
  stem?: string;
  options?: Array<{ text?: string; isCorrect?: boolean }>;
}

export interface QsLegacyExportedData {
  questions?: QsLegacyExportedQuestion[];
  difficulty?: QsExportedData['difficulty'];
  format?: QsExportedData['format'];
}

/**
 * Normalize the `quiz-supporter:exported` payload into QsExportedData, so both wire shapes funnel
 * into the same mapper. Every question inherits the set's difficulty and format, which is how the
 * embed reports them: one exam, one difficulty, one question type.
 *
 * @param data - The `data` field of a `quiz-supporter:exported` message
 * @returns QsExportedData, empty when the payload carries no questions
 */
export function normalizeLegacyExportedData(
  data: QsLegacyExportedData | undefined
): QsExportedData {
  const difficulty = data?.difficulty;
  const format = data?.format;

  return {
    questions: (data?.questions || []).map((question) => ({
      stem: question.stem,
      content: question.stem,
      options: (question.options || []).map((option) => ({
        content: option.text,
        text: option.text,
        isCorrect: Boolean(option.isCorrect),
      })),
      format,
      level: difficulty,
    })),
    difficulty,
    format,
  };
}
